/**
 * QuestionCard — DEV-539 interview accessory view.
 *
 * Renders the currently-outstanding interview question in the fixed accessory
 * slot above the chat input. Dispatches to a per-spec-type control (radio,
 * checkbox, ordered pills, textarea, number input, date input) and always
 * exposes an elaboration textarea so the user can add prose alongside a pick.
 *
 * Write-ins: every multiple-choice question ships a trailing `is_other` option
 * ("None of these - I'll write in text"). Picking it makes the elaboration
 * textarea the primary answer, and the prose is stored on the observation
 * (`ProgramObservation.value_text`). It never becomes a new option: the option
 * catalog is authored configuration shared by every program that inherits the
 * spec, so writing answers into it would leak one program's prose into other
 * programs' interviews.
 *
 * On submit, POSTs the answer directly to
 * `/api/documents/interviews/:interview_id/record-answer/` — bypassing the
 * agent turn for snappier UX — then fires an optimistic dismiss + calls the
 * silent continuation ping so the agent's next turn re-enters
 * `get_next_question` and advances.
 *
 * Structured like `EventCard` (rendering) with the pending → loading →
 * success/error state machine from `ActionSuggestionCard`.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { tokenManager } from '../lib/api';
import type {
  InterviewQuestionOption,
  InterviewQuestionPayload,
} from '../types/interview_question';

const API_URL = import.meta.env.VITE_API_URL || '';

type CardStatus = 'pending' | 'loading' | 'success' | 'error';

interface QuestionCardProps {
  question: InterviewQuestionPayload;
  /** Called after a successful record-answer POST — clears the pending state.
   *  `answerText` is a human-readable summary of what the user submitted, so
   *  the parent can render an inline "you answered X" marker.
   *  `response` is the raw JSON body; parents that want to consume the
   *  inline `next_question` / `interview_progress` (added to record-answer +
   *  skip-question responses to avoid a second round-trip) can read it here. */
  onAnswered: (questionId: string, answerText?: string, response?: any) => void;
  /** Silent continuation ping wired by the parent. */
  onContinueInterview?: (questionId: string) => void;
  /** Skip this question without recording a real answer. When provided,
   *  renders a "Skip" secondary button that fires a distinct POST to
   *  /interviews/:id/skip-question/ and calls onAnswered with the response. */
  onSkip?: (questionId: string, response?: any) => void;
  /** Compact layout inside the drawer. */
  compact?: boolean;
  /** Suppress the internal "N of M" progress hint when the parent already
   *  shows the count (e.g. in the module-page "Answer questions (N of M)" box). */
  hideProgress?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onAnswered,
  onContinueInterview,
  onSkip,
  compact = false,
  hideProgress = false,
}) => {
  const [status, setStatus] = useState<CardStatus>('pending');
  const [error, setError] = useState<string | null>(null);

  // Picked option values for categorical / multi_categorical / ordered_categorical
  // / boolean. Single-select types keep this at length ≤ 1.
  const [picks, setPicks] = useState<string[]>([]);
  // Free-text elaboration — always visible per design decision. Also holds
  // typed values for text / numeric / datetime spec_types.
  const [elaboration, setElaboration] = useState<string>('');
  // Value entered for text / numeric / datetime — separate from elaboration so
  // the primary control state is distinct from the always-on prose slot.
  const [typedValue, setTypedValue] = useState<string>('');

  const otherOption = useMemo(
    () => (question.options || []).find((o) => o.is_other),
    [question.options],
  );
  const otherPicked = otherOption ? picks.includes(otherOption.value) : false;

  // A `text` spec renders no separate control (the elaboration box IS the
  // answer), and a write-in carries the whole answer in prose. In both cases
  // the textarea is required, not the "anything to add?" afterthought its
  // default label describes.
  const elaborationRequired = otherPicked || question.spec_type === 'text';

  const canSubmit = useMemo(() => {
    if (status === 'loading') return false;
    if (question.spec_type === 'text') {
      return typedValue.trim().length > 0 || elaboration.trim().length > 0;
    }
    if (question.spec_type === 'numeric') {
      const parsed = Number(typedValue);
      return !Number.isNaN(parsed) && typedValue.trim().length > 0;
    }
    if (question.spec_type === 'datetime') {
      return typedValue.trim().length > 0;
    }
    if (
      question.spec_type === 'categorical' ||
      question.spec_type === 'boolean' ||
      question.spec_type === 'ordered_categorical'
    ) {
      // Must pick exactly one. If Other is picked, the write-in is required.
      if (picks.length !== 1) return false;
      if (otherPicked && elaboration.trim().length === 0) return false;
      return true;
    }
    if (question.spec_type === 'multi_categorical') {
      if (picks.length === 0) return false;
      if (otherPicked && elaboration.trim().length === 0) return false;
      return true;
    }
    return false;
  }, [status, question.spec_type, typedValue, elaboration, picks, otherPicked]);

  // What is still missing, in the user's words. Without this the requirement is
  // expressed only as a greyed-out button, which says nothing about what to do.
  const blockingReason = useMemo(() => {
    if (canSubmit || status === 'loading') return null;
    // Free-text / write-in answers: the empty field + placeholder are hint
    // enough — no footer nag (DEV-732 simplification).
    if (otherPicked && elaboration.trim().length === 0) {
      return null;
    }
    switch (question.spec_type) {
      case 'text':
        return null;
      case 'numeric':
        return 'Enter a number to continue.';
      case 'datetime':
        return 'Pick a date to continue.';
      case 'multi_categorical':
        return 'Choose at least one option to continue.';
      default:
        return 'Choose an option to continue.';
    }
  }, [canSubmit, status, otherPicked, elaboration, question.spec_type]);

  const buildBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      question: question.spec_id,
      question_id: question.question_id,
      // 1=Low, 3=Medium, 5=High (ProgramObservation.confidence). A respondent
      // picking a curated option is a first-hand, unambiguous answer about
      // their own program — High. A write-in is prose nobody has mapped to the
      // spec's vocabulary yet, so it lands at Medium and can be raised when a
      // human reconciles it. Confidence feeds downstream observation weighting,
      // so a single constant across both would flatten a real difference.
      confidence: otherPicked ? 3 : 5,
    };
    if (elaboration.trim()) {
      body.elaboration_text = elaboration.trim();
    }

    switch (question.spec_type) {
      case 'text':
        body.value = elaboration.trim() || typedValue.trim();
        break;
      case 'numeric':
        body.value = Number(typedValue);
        break;
      case 'datetime':
        body.value = typedValue;
        break;
      case 'boolean':
      case 'categorical':
      case 'ordered_categorical':
        // Includes the `__other__` sentinel when the user wrote in: the backend
        // anchors the observation to the is_other row and stores the prose in
        // value_text.
        body.value = picks[0];
        break;
      case 'multi_categorical':
        body.value = picks;
        break;
    }

    return body;
  };

  /**
   * Build the human-readable summary that renders as the user's answer bubble
   * in the message thread. Prefers picked-option labels over raw values so the
   * user sees "Urgent" rather than "urgent" or "__other__".
   */
  const buildAnswerText = (): string => {
    const optionLabel = (v: string) =>
      (question.options || []).find((o) => o.value === v)?.label || v;

    if (question.spec_type === 'text') {
      return (elaboration.trim() || typedValue.trim()).slice(0, 240);
    }
    if (question.spec_type === 'numeric') {
      return question.unit ? `${typedValue} ${question.unit}` : typedValue;
    }
    if (question.spec_type === 'datetime') {
      return typedValue;
    }
    // categorical / multi_categorical / ordered_categorical / boolean
    // A write-in shows the prose, not the "None of these" label.
    if (otherPicked && elaboration.trim()) {
      const realPicks = picks
        .filter((v) => v !== otherOption?.value)
        .map(optionLabel);
      return [...realPicks, elaboration.trim()].join(', ');
    }
    const combined = picks.map(optionLabel).join(', ');
    if (elaboration.trim()) {
      return `${combined} - ${elaboration.trim()}`;
    }
    return combined;
  };

  // `canSubmit` is a render-time memo, so two events dispatched before React
  // re-renders both read a stale `true` — Enter in the textarea racing the
  // form's own submit, or a double click. The ref flips synchronously, so the
  // second caller sees it and drops out.
  const submittingRef = useRef(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setStatus('loading');
    setError(null);
    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(
        `${API_URL}/api/documents/interviews/${question.interview_id}/record-answer/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(buildBody()),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.detail || `record-answer failed (${response.status})`);
      }
      const body = await response.json().catch(() => ({}));
      setStatus('success');
      // Pass the full response through so parents can consume the inline
      // next_question / interview_progress fields the endpoint now returns.
      onAnswered(question.question_id, buildAnswerText(), body);
      // Fire-and-forget continuation ping so the agent picks up the answer and
      // advances to the next question without a user prompt.
      // The parent owns failure handling: this card is already unmounting via
      // onAnswered, so it has nowhere to show an error. See ProgramAgent's
      // onContinueInterview, which surfaces a "send any message to continue"
      // toast if the ping never lands.
      onContinueInterview?.(question.question_id);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStatus('error');
    } finally {
      // Released so the error state is retryable. The success state renders no
      // form at all, so it cannot re-submit.
      submittingRef.current = false;
    }
  };

  const handleSkip = async () => {
    if (!onSkip || submittingRef.current) return;
    submittingRef.current = true;
    setStatus('loading');
    setError(null);
    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(
        `${API_URL}/api/documents/interviews/${question.interview_id}/skip-question/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            spec_id: question.spec_id,
            question_id: question.question_id,
          }),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.detail || `skip-question failed (${response.status})`);
      }
      const body = await response.json().catch(() => ({}));
      setStatus('success');
      onSkip(question.question_id, body);
      // Best-effort agent ping (mirrors the record-answer path).
      onContinueInterview?.(question.question_id);
    } catch (err: any) {
      setError(err.message || 'Could not skip question');
      setStatus('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const wrapperClass = `flex flex-col gap-3 ${
    compact ? 'px-3 py-3' : 'px-4 py-4'
  } bg-card border border-border rounded-lg`;

  // --- Loading state ---
  if (status === 'loading') {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-muted border border-border rounded-lg`}>
        <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
        <span className={`text-muted-foreground ${compact ? 'text-xs' : 'text-sm'}`}>
          Recording answer…
        </span>
      </div>
    );
  }

  // --- Success state (transient — parent will remove the card via onAnswered) ---
  if (status === 'success') {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-muted border border-border rounded-lg`}>
        <CheckCircle className="w-4 h-4 text-foreground shrink-0" />
        <span className={`text-foreground ${compact ? 'text-xs' : 'text-sm'}`}>
          Answer recorded.
        </span>
      </div>
    );
  }

  // --- Pending / error state ---
  //
  // Wrapping the card in a <form> gives us Enter-to-submit for free on any
  // non-textarea input. Textareas retain the browser default (Enter = newline),
  // except where the textarea IS the answer — see the elaboration onKeyDown.
  return (
    <form onSubmit={handleSubmit} className={wrapperClass}>
      {/* Header: "N of M" progress hint (if provided) + question prompt. */}
      <div className="flex flex-col gap-1.5">
        {!hideProgress &&
          typeof question.question_index === 'number' &&
          typeof question.total_questions === 'number' && (
            <span
              className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} tracking-wide`}
            >
              {/* question_index is 0-based from the backend; display 1-based. */}
              {question.question_index + 1} of {question.total_questions}
            </span>
          )}
        <p className={`font-medium text-foreground ${compact ? 'text-sm' : 'text-[15px]'}`}>
          {question.prompt}
        </p>
      </div>

      <QuestionControl
        question={question}
        picks={picks}
        setPicks={setPicks}
        typedValue={typedValue}
        setTypedValue={setTypedValue}
        compact={compact}
      />

      {question.allow_elaboration && (
        <div className="flex flex-col gap-1">
          {/* The label is only useful for the optional-context case; for a
              primary/write-in answer the placeholder + aria-label carry it,
              so we drop the visible "Your answer" chrome (DEV-732). */}
          {!(otherPicked || question.spec_type === 'text') && (
            <label
              htmlFor={`elab-${question.question_id}`}
              className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} opacity-80`}
            >
              Anything to add? (optional)
            </label>
          )}
          <textarea
            id={`elab-${question.question_id}`}
            value={elaboration}
            required={elaborationRequired}
            aria-required={elaborationRequired}
            aria-label={
              otherPicked
                ? 'Your answer (write in text)'
                : question.spec_type === 'text'
                  ? 'Your answer'
                  : undefined
            }
            onChange={(e) => setElaboration(e.target.value)}
            onKeyDown={(e) => {
              // For text/other-picked (elaboration IS the primary answer):
              //   Enter submits, Shift+Enter inserts a newline.
              // For everything else (elaboration is optional prose alongside a
              //   pick): Cmd/Ctrl+Enter submits, Enter inserts a newline as
              //   usual — user might want multi-line notes.
              const submitCombo = elaborationRequired
                ? e.key === 'Enter' && !e.shiftKey
                : e.key === 'Enter' && (e.metaKey || e.ctrlKey);
              if (submitCombo) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            rows={elaborationRequired ? 3 : 2}
            placeholder={
              otherPicked
                ? 'Describe the answer that fits…'
                : question.spec_type === 'text'
                  ? 'Type your answer…'
                  : 'Optional context or caveats…'
            }
            className={`w-full ${compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2'} bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-y`}
          />
        </div>
      )}

      {status === 'error' && error && (
        // role="alert" so a submission failure is announced. Without it the
        // only change a screen reader can observe is the button re-enabling.
        <div
          role="alert"
          className={`flex items-start gap-2 ${compact ? 'text-xs' : 'text-sm'} text-destructive`}
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Footer: what's still missing on the left, Skip + submit on the right. */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'}`}>
          {blockingReason}
        </span>
        <div className="flex items-center gap-2">
          {onSkip && (
            <button
              type="button"
              onClick={handleSkip}
              title="Skip this question"
              className={[
                'text-muted-foreground hover:text-foreground transition-colors',
                compact ? 'text-[11px]' : 'text-xs',
                'underline-offset-2 hover:underline',
              ].join(' ')}
            >
              Skip
            </button>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            title={status === 'error' ? 'Retry' : 'Submit answer'}
            aria-label={status === 'error' ? 'Retry' : 'Submit answer'}
            className={[
              'flex items-center justify-center rounded-full transition-colors shrink-0',
              compact ? 'w-9 h-9' : 'w-10 h-10',
              'bg-foreground text-background',
              'disabled:opacity-40 disabled:cursor-not-allowed',
              'hover:bg-foreground/85',
            ].join(' ')}
          >
            <ArrowRight className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          </button>
        </div>
      </div>
    </form>
  );
};

// --- Per-spec-type control dispatcher ---

interface QuestionControlProps {
  question: InterviewQuestionPayload;
  picks: string[];
  setPicks: React.Dispatch<React.SetStateAction<string[]>>;
  typedValue: string;
  setTypedValue: React.Dispatch<React.SetStateAction<string>>;
  compact: boolean;
}

const QuestionControl: React.FC<QuestionControlProps> = (props) => {
  switch (props.question.spec_type) {
    case 'categorical':
    case 'boolean':
      return <RadioControl {...props} />;
    case 'multi_categorical':
      return <CheckboxControl {...props} />;
    case 'ordered_categorical':
      return <OrderedPillsControl {...props} />;
    case 'text':
      // Text specs use the elaboration textarea as the primary control — no
      // separate control needed. Show a hint if the elaboration is hidden.
      return props.question.allow_elaboration ? null : <TextControl {...props} />;
    case 'numeric':
      return <NumericControl {...props} />;
    case 'datetime':
      return <DatetimeControl {...props} />;
    default:
      return null;
  }
};

/**
 * The roving-tabindex half of the ARIA radiogroup pattern: one tab stop for the
 * whole group, arrow keys move focus and selection within it. Both single-select
 * controls here claim `role="radio"`, so both owe the behaviour the role
 * announces. Multi-select is a group of checkboxes — each is its own tab stop
 * and arrow keys are not part of that pattern — so it passes `enabled: false`.
 */
function useRovingRadio(
  options: InterviewQuestionOption[],
  selectedValue: string | undefined,
  onSelect: (value: string) => void,
  enabled: boolean,
) {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((o) => o.value === selectedValue);
  const focusIndex = selectedIndex >= 0 ? selectedIndex : activeIndex;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!enabled || options.length === 0) return;
    const delta =
      e.key === 'ArrowDown' || e.key === 'ArrowRight'
        ? 1
        : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
          ? -1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = (focusIndex + delta + options.length) % options.length;
    setActiveIndex(next);
    onSelect(options[next].value);
    itemRefs.current[next]?.focus();
  };

  return {
    onKeyDown,
    /** Checkboxes stay individually tabbable; radios expose a single tab stop. */
    tabIndexFor: (index: number) => (!enabled || index === focusIndex ? 0 : -1),
    registerRef: (index: number) => (el: HTMLButtonElement | null) => {
      itemRefs.current[index] = el;
    },
  };
}

// --- Basic controls ---
// The MC tile layout matches the Figma "Interview 1, selections" design:
// stacked full-width cards with title + optional description, subtle border,
// and a filled dot on the right edge to indicate selection. Single-select
// (radio) and multi-select (checkbox) share the same visual — only the
// toggle semantics differ.

interface OptionTileListProps {
  question: InterviewQuestionPayload;
  options: InterviewQuestionOption[];
  isSelected: (value: string) => boolean;
  onToggle: (value: string) => void;
  compact: boolean;
  multi: boolean;
}

const OptionTileList: React.FC<OptionTileListProps> = ({
  question,
  options,
  isSelected,
  onToggle,
  compact,
  multi,
}) => {
  const selectedValue = options.find((o) => isSelected(o.value))?.value;
  const roving = useRovingRadio(options, selectedValue, onToggle, !multi);
  return (
    <div
      role={multi ? 'group' : 'radiogroup'}
      aria-label={question.prompt}
      onKeyDown={roving.onKeyDown}
      className="flex flex-col gap-2"
    >
      {options.map((opt, index) => {
        const selected = isSelected(opt.value);
        return (
          <button
            key={opt.value}
            ref={roving.registerRef(index)}
            type="button"
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={selected}
            tabIndex={roving.tabIndexFor(index)}
            onClick={() => onToggle(opt.value)}
            className={[
              'flex items-center justify-between gap-3 w-full text-left transition-colors',
              compact ? 'px-3 py-2.5' : 'px-4 py-3',
              'rounded-lg bg-background border',
              // The escape hatch is not one of the authored answers. A dashed
              // edge reads as "off the list" at a glance, where the previous
              // italic-only treatment did not.
              opt.is_other ? 'border-dashed' : '',
              selected
                ? 'border-foreground/60 shadow-[0_0_0_1px_var(--foreground)]'
                : 'border-border hover:border-foreground/30',
            ].join(' ')}
          >
            <span className="flex-1 min-w-0">
              <span
                className={`block font-medium text-foreground ${compact ? 'text-sm' : 'text-[15px]'}`}
              >
                {opt.label}
              </span>
              {opt.description && (
                <span
                  className={`block text-muted-foreground mt-0.5 ${compact ? 'text-xs' : 'text-sm'}`}
                >
                  {opt.description}
                </span>
              )}
            </span>
            <span
              aria-hidden
              className={[
                'shrink-0 rounded-full transition-colors',
                compact ? 'w-2 h-2' : 'w-2.5 h-2.5',
                selected ? 'bg-foreground' : 'bg-transparent border border-border',
              ].join(' ')}
            />
          </button>
        );
      })}
    </div>
  );
};

const RadioControl: React.FC<QuestionControlProps> = ({
  question,
  picks,
  setPicks,
  compact,
}) => (
  <OptionTileList
    question={question}
    options={question.options || []}
    isSelected={(v) => picks[0] === v}
    onToggle={(v) => setPicks([v])}
    compact={compact}
    multi={false}
  />
);

const CheckboxControl: React.FC<QuestionControlProps> = ({
  question,
  picks,
  setPicks,
  compact,
}) => (
  <OptionTileList
    question={question}
    options={question.options || []}
    isSelected={(v) => picks.includes(v)}
    onToggle={(v) =>
      setPicks((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
    }
    compact={compact}
    multi
  />
);

const TextControl: React.FC<QuestionControlProps> = ({
  question,
  typedValue,
  setTypedValue,
  compact,
}) => (
  <textarea
    value={typedValue}
    onChange={(e) => setTypedValue(e.target.value)}
    rows={3}
    placeholder={question.prompt}
    className={`w-full ${compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2'} bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-y`}
  />
);

const NumericControl: React.FC<QuestionControlProps> = ({
  question,
  typedValue,
  setTypedValue,
  compact,
}) => {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={typedValue}
        onChange={(e) => setTypedValue(e.target.value)}
        placeholder={question.unit ? `Value (${question.unit})` : 'Value'}
        className={`flex-1 ${compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2'} bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring`}
      />
      {question.unit && (
        <span className={`text-muted-foreground ${compact ? 'text-xs' : 'text-sm'}`}>
          {question.unit}
        </span>
      )}
    </div>
  );
};

// Date-only on purpose. Every seeded `datetime` ProgramSpec is an acquisition
// milestone (planned_rfp_release_date, selection_deadline, expected_award_date),
// and none of them has a meaningful time of day. `datetime-local` would make a
// respondent invent an hour to get past the control. `value_datetime` keeps the
// full precision if a future spec ever needs it - swap the input then.
const DatetimeControl: React.FC<QuestionControlProps> = ({
  typedValue,
  setTypedValue,
  compact,
}) => (
  <input
    type="date"
    value={typedValue}
    onChange={(e) => setTypedValue(e.target.value)}
    className={`w-full ${compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2'} bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring`}
  />
);

// --- Ordered pills [design-blocked default: horizontal ordered chevron pills] ---
//
// The final interaction pattern (drag-to-reorder / click-to-append-rank / up-down
// chevrons) is pending a designer spec. Interim: render options as horizontal
// pills in authored order (which for ordered_categorical IS the ranking) and let
// the user pick one — same interaction as a radio, but visually conveys the
// ordering. Swap for the real component once the Figma frame lands.
//
// The `is_other` escape hatch is deliberately NOT in that row: the pill sequence
// is the ranking, so "None of these" sitting in it reads as a rank on the scale.
// It goes below a rule, outside the ramp, where it is an exit rather than a step.

const OrderedPillsControl: React.FC<QuestionControlProps> = ({
  question,
  picks,
  setPicks,
  compact,
}) => {
  const options = question.options || [];
  const ramp = options.filter((o) => !o.is_other);
  const other = options.find((o) => o.is_other);
  // Keyboard order follows the visual order: along the ramp, then the exit.
  const ordered = other ? [...ramp, other] : ramp;
  const roving = useRovingRadio(ordered, picks[0], (v) => setPicks([v]), true);

  const pill = (opt: InterviewQuestionOption, index: number) => {
    const active = picks[0] === opt.value;
    return (
      <button
        key={opt.value}
        ref={roving.registerRef(index)}
        type="button"
        role="radio"
        aria-checked={active}
        tabIndex={roving.tabIndexFor(index)}
        onClick={() => setPicks([opt.value])}
        className={`${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-full border transition-colors ${
          active
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-background text-foreground border-border hover:bg-accent'
        } ${opt.is_other ? 'border-dashed' : ''}`}
      >
        {opt.label}
      </button>
    );
  };

  return (
    <div
      role="radiogroup"
      aria-label={question.prompt}
      onKeyDown={roving.onKeyDown}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-wrap gap-1.5">
        {ramp.map((opt, index) => pill(opt, index))}
      </div>
      {other && (
        <div className="pt-2 border-t border-border">{pill(other, ramp.length)}</div>
      )}
    </div>
  );
};

export default QuestionCard;
